import { useMemo } from "react";

import { useAuth } from "react-oidc-context";

/**
 * Decodes the partner_service_acls claim from the JWT token
 * Returns a map of serviceId -> Set of ACL permissions
 * Format: [service count][serviceId][bit count][byte length][bit bytes]...
 */
const decodeServiceAcls = (serviceAclsHash: string): Map<number, Set<number>> => {
  const servicePermissions = new Map<number, Set<number>>();

  if (!serviceAclsHash) {
    return servicePermissions;
  }

  try {
    // Decode Base64 string to binary
    const binaryString = atob(serviceAclsHash);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    let offset = 0;

    // Read service count (4 bytes, little-endian)
    const serviceCount =
      bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] << 24);
    offset += 4;

    // Read each service and its ACL bits
    for (let s = 0; s < serviceCount; s++) {
      // Read serviceId (4 bytes, little-endian)
      const serviceId =
        bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] << 24);
      offset += 4;

      // Read bit count (4 bytes, little-endian)
      const bitCount = bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] << 24);
      offset += 4;

      // Read byte length (4 bytes, little-endian)
      const byteLength =
        bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] << 24);
      offset += 4;

      // Read bit bytes
      const bitBytes = bytes.slice(offset, offset + byteLength);
      offset += byteLength;

      // Convert bytes to ACL set
      const acls = new Set<number>();
      for (let bitIndex = 0; bitIndex < bitCount; bitIndex++) {
        const byteIndex = Math.floor(bitIndex / 8);
        const bitPosition = bitIndex % 8;
        if (byteIndex < bitBytes.length && (bitBytes[byteIndex] & (1 << bitPosition)) !== 0) {
          acls.add(bitIndex + 1); // ACL values are 1-based
        }
      }

      if (acls.size > 0) {
        servicePermissions.set(serviceId, acls);
      }
    }
  } catch (error) {
    console.error("Failed to decode partner_service_acls claim:", error);
  }

  return servicePermissions;
};

/**
 * ACL enum values for Partner Portal permissions
 */
export enum PartnerPortalAcl {
  ContactManagement = 317,
  TermDates = 318,
  ConfirmChildren = 319,
  AllServices = 320, // partner_all_services - grants access to all services (admin)
}

/**
 * Decodes the global acl claim from the JWT token
 * Returns a Set of ACL permission values
 * Format: Raw bytes where each bit represents an ACL (bit 0 = ACL 1, bit 316 = ACL 317, etc.)
 */
const decodeGlobalAcls = (aclHash: string): Set<number> => {
  const globalPermissions = new Set<number>();

  if (!aclHash) {
    return globalPermissions;
  }

  try {
    // Decode Base64 string to binary
    const binaryString = atob(aclHash);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Each bit represents an ACL permission
    // Bit 0 = ACL 1, Bit 316 = ACL 317, etc.
    const totalBits = bytes.length * 8;
    for (let bitIndex = 0; bitIndex < totalBits; bitIndex++) {
      const byteIndex = Math.floor(bitIndex / 8);
      const bitPosition = bitIndex % 8;
      if ((bytes[byteIndex] & (1 << bitPosition)) !== 0) {
        globalPermissions.add(bitIndex + 1); // ACL values are 1-based
      }
    }
  } catch (error) {
    console.error("Failed to decode acl claim:", error);
  }

  return globalPermissions;
};

/**
 * Hook to check service-scoped permissions from JWT token
 * Also checks global ACL for partner_all_services (admin access)
 */
export const useServicePermissions = () => {
  const auth = useAuth();

  const { servicePermissions, globalPermissions } = useMemo(() => {
    if (!auth.user?.access_token) {
      return {
        servicePermissions: new Map<number, Set<number>>(),
        globalPermissions: new Set<number>(),
      };
    }

    try {
      // Decode JWT payload (second part of the token)
      const parts = auth.user.access_token.split(".");
      if (parts.length !== 3) {
        console.error("Invalid JWT token format");
        return {
          servicePermissions: new Map<number, Set<number>>(),
          globalPermissions: new Set<number>(),
        };
      }

      const payload = JSON.parse(atob(parts[1]));

      // Decode global ACL claim
      const aclHash = payload.acl;
      const decodedGlobalPermissions = aclHash ? decodeGlobalAcls(aclHash) : new Set<number>();

      // Decode service-specific ACL claim
      const serviceAclsHash = payload.partner_service_acls;
      const decodedServicePermissions = serviceAclsHash
        ? decodeServiceAcls(serviceAclsHash)
        : new Map<number, Set<number>>();

      return {
        servicePermissions: decodedServicePermissions,
        globalPermissions: decodedGlobalPermissions,
      };
    } catch (error) {
      console.error("Failed to parse JWT token:", error);
      return {
        servicePermissions: new Map<number, Set<number>>(),
        globalPermissions: new Set<number>(),
      };
    }
  }, [auth.user?.access_token]);

  /**
   * Check if user has access to all services (admin)
   */
  const hasAllServicesAccess = (): boolean => {
    return globalPermissions.has(PartnerPortalAcl.AllServices);
  };

  /**
   * Check if user has a specific permission for a service
   * Logic matches UserHasServicePermissionAttribute.cs:
   * 1. First check if user has the ACL in global acl claim AND has partner_all_services
   * 2. Then check service-specific permission in partner_service_acls claim
   */
  const hasServicePermission = (serviceId: number, acl: PartnerPortalAcl): boolean => {
    // First check if user has the permission globally (standard ACL claim) AND has all-services permission
    // This matches the C# logic: aclService.ContainsPermission(user, AllServicesPermission) && aclService.ContainsPermission(user, RequiredPermission.Value)
    if (globalPermissions.has(acl) && hasAllServicesAccess()) {
      return true; // User has global permission, grant access
    }

    // Check if user has the required permission for this specific service
    const permissions = servicePermissions.get(serviceId);
    return permissions?.has(acl) ?? false;
  };

  /**
   * Check if user has any permissions for a service
   * Returns true if user has partner_all_services OR any permission for the specific service
   */
  const hasAnyServicePermission = (serviceId: number): boolean => {
    // Admin with partner_all_services has access to everything
    if (hasAllServicesAccess()) {
      return true;
    }

    const permissions = servicePermissions.get(serviceId);
    return permissions !== undefined && permissions.size > 0;
  };

  /**
   * Get all permissions for a service
   */
  const getServicePermissions = (serviceId: number): Set<number> => {
    return servicePermissions.get(serviceId) ?? new Set<number>();
  };

  /**
   * Get all services the user has permissions for
   * Note: For admin users with partner_all_services, this returns only explicitly assigned services
   */
  const getAuthorizedServices = (): number[] => {
    return Array.from(servicePermissions.keys());
  };

  /**
   * Check if user has a global ACL permission
   */
  const hasGlobalPermission = (acl: number): boolean => {
    return globalPermissions.has(acl);
  };

  return {
    servicePermissions,
    globalPermissions,
    hasServicePermission,
    hasAnyServicePermission,
    getServicePermissions,
    getAuthorizedServices,
    hasAllServicesAccess,
    hasGlobalPermission,
  };
};
